import { useMemo, useState, useEffect, useRef } from 'react';
import { countUniqueNums } from '../utils/groupUtils';
import { STATUTS } from '../utils/constants';
import { useSeriesSettings, loadTotals, saveTotals, loadHsTotals, saveHsTotals, findMissingTomes, findDuplicateTomes } from '../utils/seriesTotals';
import ShelfModal from './ShelfModal';
import Mascot from './Mascot';

const T = {
  orange:'var(--orange)', orangeD:'var(--orange-d)', ink:'var(--ink)', surface:'var(--surface)',
  border:'var(--border)', borderMd:'var(--border-md)', muted:'var(--muted)', subtle:'var(--subtle)',
  g1:'var(--surface2)', green:'#16A34A', red:'#DC2626', yellow:'#F5C842', blue:'#2563EB',
};
const FONT    = "'Inter',-apple-system,sans-serif";

const getStatut = v => STATUTS.find(s=>s.value===v)||STATUTS[0];

// ── Barre de progression ───────────────────────────────────────────────────────
const Bar = ({ pct, color=T.orange, height=5 }) => (
  <div style={{height,background:T.border,borderRadius:'99px',overflow:'hidden',flex:1}}>
    <div style={{height:'100%',width:`${Math.min(pct,100)}%`,background:color,borderRadius:'99px',transition:'width .5s ease'}}/>
  </div>
);

// ── Couleurs d'état ───────────────────────────────────────────────────────────
const ETAT_COLORS = {
  neuf:       '#16A34A',
  tres_bon:   '#2563EB',
  bon:        '#CA8A04',
  acceptable: '#EA580C',
  mauvais:    '#DC2626',
};
const ETAT_LABELS = {
  neuf:'Neuf', tres_bon:'Très bon état', bon:'Bon état',
  acceptable:'Acceptable', mauvais:'Mauvais état',
};

// ── Miniature livre ────────────────────────────────────────────────────────────
const BookThumb = ({ bd, onClick }) => {
  const s = getStatut(bd.statut);
  const p = bd.photos?.[0];
  const etatColor = ETAT_COLORS[bd.etatGeneral];
  const hasDefauts = bd.defauts?.length > 0;

  const tooltipLines = [
    bd.titre || (bd.serie ? bd.serie + (bd.numero ? ' T.' + bd.numero : '') : '(Sans titre)'),
    s.label,
    bd.etatGeneral ? ETAT_LABELS[bd.etatGeneral] : '',
    hasDefauts ? bd.defauts.length + ' défaut' + (bd.defauts.length > 1 ? 's' : '') : '',
  ].filter(Boolean).join(' · ');

  // Bordure : couleur d'état bien visible, effet néon en mode sombre via box-shadow
  const borderColor = etatColor || T.border;
  const glowShadow  = etatColor
    ? `0 0 0 2px ${etatColor}, 0 0 8px ${etatColor}88`
    : '0 1px 3px rgba(0,0,0,.1)';

  return (
    <div onClick={() => onClick(bd)} title={tooltipLines}
      style={{
        position:'relative', width:'34px', height:'48px',
        borderRadius:'4px', overflow:'hidden', cursor:'pointer',
        border: `2px solid ${borderColor}`,
        flexShrink:0,
        background: p ? '#000' : 'linear-gradient(160deg,var(--orange-pale),var(--surface2))',
        boxShadow: etatColor ? `0 0 0 1px ${etatColor}55` : '0 1px 3px rgba(0,0,0,.1)',
        transition:'transform .15s, box-shadow .15s',
      }}
      onMouseOver={e => {
        e.currentTarget.style.transform = 'scale(1.15) translateY(-3px)';
        e.currentTarget.style.boxShadow = glowShadow;
        e.currentTarget.style.zIndex = '10';
      }}
      onMouseOut={e => {
        e.currentTarget.style.transform = 'none';
        e.currentTarget.style.boxShadow = etatColor ? `0 0 0 1px ${etatColor}55` : '0 1px 3px rgba(0,0,0,.1)';
        e.currentTarget.style.zIndex = 'auto';
      }}>
      {p
        ? <img src={p.url} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}}/>
        : <div style={{height:'100%',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'14px',opacity:.4}}>📚</div>
      }
      {/* Point statut */}
      <div style={{position:'absolute',bottom:'2px',right:'2px',width:'7px',height:'7px',borderRadius:'50%',background:s.color,border:'1.5px solid rgba(255,255,255,.85)'}}/>
      {/* Numéro */}
      {bd.numero && (
        <div style={{position:'absolute',top:0,left:0,right:0,background:'rgba(28,22,18,.65)',color:'#fff',fontSize:'8px',fontWeight:700,textAlign:'center',padding:'1px',lineHeight:1.4,fontFamily:FONT}}>
          {bd.numero}
        </div>
      )}
      {/* Petit triangle défaut en haut à gauche */}
      {hasDefauts && (
        <div style={{position:'absolute',top:'2px',left:'2px',width:'7px',height:'7px',background:'#DC2626',borderRadius:'50%',border:'1px solid rgba(255,255,255,.8)'}}/>
      )}
    </div>
  );
};

// ── Champ "total de tomes" éditable ───────────────────────────────────────────
export function TotalEditor({ serie, value, onChange, unit = 'tomes numérotés', emptyLabel = '+ Nb. de tomes numérotés', prefix = '', title = 'Nombre de tomes numérotés officiellement dans la série (les hors-séries ne comptent pas)' }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState(value?.toString() || '');
  const inputRef = useRef(null);

  const commit = () => {
    const n = parseInt(draft);
    onChange(isNaN(n)||n<1 ? null : n);
    setEditing(false);
  };

  if (editing) return (
    <form onSubmit={e=>{e.preventDefault();commit();}} style={{display:'inline-flex',alignItems:'center',gap:'4px'}}>
      <input
        ref={inputRef} type="number" value={draft}
        onChange={e=>setDraft(e.target.value)}
        onBlur={commit} onKeyDown={e=>e.key==='Escape'&&setEditing(false)}
        min="1" max="9999" autoFocus
        style={{width:'52px',padding:'3px 6px',border:`1.5px solid ${T.orange}`,borderRadius:'6px',fontFamily:FONT,fontSize:'12px',fontWeight:700,color:T.ink,outline:'none',textAlign:'center',boxShadow:`0 0 0 3px rgba(240,78,26,.12)`}}/>
      <button type="submit" style={{background:T.orange,border:'none',borderRadius:'4px',color:'#fff',fontSize:'10px',padding:'3px 6px',cursor:'pointer',fontWeight:700,fontFamily:FONT}}>OK</button>
    </form>
  );

  return (
    <button type="button" onClick={()=>{setDraft(value?.toString()||'');setEditing(true);}}
      title={title}
      style={{display:'inline-flex',alignItems:'center',gap:'4px',background:'transparent',border:`1px dashed ${value?T.borderMd:T.border}`,borderRadius:'6px',padding:'3px 8px',cursor:'pointer',fontFamily:FONT,fontSize:'12px',fontWeight:value?700:400,color:value?T.muted:T.subtle,transition:'all .15s'}}
      onMouseOver={e=>{e.currentTarget.style.borderColor=T.orange;e.currentTarget.style.color=T.orange;e.currentTarget.style.background='var(--orange-pale)';}}
      onMouseOut={e=>{e.currentTarget.style.borderColor=value?T.borderMd:T.border;e.currentTarget.style.color=value?T.muted:T.subtle;e.currentTarget.style.background='transparent';}}>
      {value ? `${prefix}/ ${value} ${unit}` : emptyLabel}
      <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1.5 5h7M5 1.5v7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>
    </button>
  );
}

// ── Carte série ────────────────────────────────────────────────────────────────
function SerieCard({ serie, bds, onEditBD, totals, onTotalChange, hsTotals, onHsChange, onShelf }) {
  const [expanded, setExpanded] = useState(false);

  const lus      = bds.filter(b=>b.lu).length;
  const uniqueNums = countUniqueNums(bds); // numéros uniques (sans doublons)
  const toVendre = bds.filter(b=>b.statut==='vendre').length;
  const firstP   = bds.find(b=>b.photos?.[0])?.photos?.[0];
  const sorted   = [...bds].sort((a,b)=>(parseInt(a.numero)||0)-(parseInt(b.numero)||0));

  // Total connu pour cette série
  const total   = totals[serie] || null;
  const pctSerie = total ? Math.round(uniqueNums/total*100) : null;
  const pctLus  = bds.length ? Math.round(lus/bds.length*100) : 0;

  // Éditeur dominant
  const edMap = bds.map(b=>b.editeur).filter(Boolean)
    .reduce((a,e)=>{a[e]=(a[e]||0)+1;return a;},{});
  const topEd = Object.entries(edMap).sort((a,b)=>b[1]-a[1])[0]?.[0];

  // Complet = tous les numéros 1..total sont possédés (les hors-séries, sans
  // numéro, ne comptent ni pour ni contre — voir findMissingTomes)
  const missingTomes = total ? findMissingTomes(bds, total) : null;
  const complete = total !== null && missingTomes.length === 0;

  // Détail du compteur : tomes numérotés distincts, doublons, hors-séries
  const horsSerie = bds.filter(b => !String(b.numero||'').trim()).length;
  const avecNumero = bds.length - horsSerie;
  const doublons = avecNumero - uniqueNums;
  const dupTomes = doublons > 0 ? findDuplicateTomes(bds) : [];

  return (
    <div style={{background:T.surface,border:`1px solid ${complete?T.green:T.border}`,borderRadius:'14px',overflow:'hidden',boxShadow:complete?`0 1px 4px rgba(22,163,74,.12)`:'0 1px 4px rgba(0,0,0,.05)',transition:'box-shadow .2s, border-color .2s'}}
      onMouseOver={e=>e.currentTarget.style.boxShadow=complete?'0 4px 16px rgba(22,163,74,.15)':'0 4px 16px rgba(0,0,0,.09)'}
      onMouseOut={e=>e.currentTarget.style.boxShadow=complete?'0 1px 4px rgba(22,163,74,.12)':'0 1px 4px rgba(0,0,0,.05)'}>

      {/* En-tête */}
      <div style={{padding:'14px 16px',cursor:'pointer',display:'flex',alignItems:'center',gap:'12px',background:expanded?'#FFF8F5':T.surface,transition:'background .15s'}}
        onClick={()=>setExpanded(x=>!x)}>

        {/* Couverture */}
        <div style={{width:'42px',height:'58px',borderRadius:'7px',overflow:'hidden',border:`1px solid ${T.border}`,background:'var(--orange-pale)',flexShrink:0,boxShadow:'0 1px 4px rgba(0,0,0,.08)'}}>
          {firstP
            ?<img src={firstP.url} alt={serie} style={{width:'100%',height:'100%',objectFit:'cover'}}/>
            :<div style={{height:'100%',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'1.3rem',opacity:.35}}>📚</div>}
        </div>

        {/* Infos */}
        <div style={{flex:1,minWidth:0}}>
          <div style={{display:'flex',alignItems:'center',gap:'7px',flexWrap:'wrap',marginBottom:'3px'}}>
            <div style={{fontSize:'15px',fontWeight:700,color:T.ink,fontFamily:FONT,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{serie}</div>
            {complete && <div style={{background:'#F0FDF4',color:'#15803D',border:'1px solid #BBF7D0',borderRadius:'99px',fontSize:'10px',fontWeight:700,padding:'1px 7px',flexShrink:0,fontFamily:FONT}}>✓ Complète</div>}
          </div>

          {/* Compteur tomes */}
          <div style={{display:'flex',alignItems:'center',gap:'6px',marginBottom:'5px',flexWrap:'wrap'}}>
            <span style={{fontSize:'13px',fontWeight:700,color:T.orange,fontFamily:FONT}}>
              {uniqueNums} tome{uniqueNums>1?'s':''}
              {(doublons>0||horsSerie>0)&&(
                <span style={{fontSize:'11px',fontWeight:600,color:T.muted}}>
                  {' ('}
                  {doublons>0&&`${doublons} en double${dupTomes.length?` : T.${dupTomes.join(', T.')}`:''}`}
                  {doublons>0&&horsSerie>0&&', '}
                  {horsSerie>0&&`${horsSerie} hors-série${horsSerie>1?'s':''}`}
                  {')'}
                </span>
              )}
            </span>
            <div onClick={e=>e.stopPropagation()}>
              <TotalEditor serie={serie} value={total} onChange={v=>onTotalChange(serie,v)}/>
            </div>
            {(horsSerie>0 || hsTotals[serie]) && (
              <div onClick={e=>e.stopPropagation()} style={{display:'inline-flex',alignItems:'center',gap:'5px'}}>
                <TotalEditor serie={serie+'-hs'} value={hsTotals[serie]||null} onChange={v=>onHsChange(serie,v)}
                  unit={'hors-série'+((hsTotals[serie]||0)>1?'s':'')} prefix={`${horsSerie} `} emptyLabel={`${horsSerie} hors-série${horsSerie>1?'s':''} · + total`}
                  title="Nombre total de hors-séries existants pour cette série (ex : 8). Sert à savoir combien il t'en manque."/>
                {hsTotals[serie]>horsSerie && <span style={{fontSize:'11px',fontWeight:700,color:T.red,fontFamily:FONT}}>il en manque {hsTotals[serie]-horsSerie}</span>}
              </div>
            )}
            {topEd && <span style={{fontSize:'11px',background:T.g1,color:T.muted,borderRadius:'4px',padding:'1px 6px',fontFamily:FONT}}>{topEd}</span>}
            {toVendre>0 && <span style={{fontSize:'11px',background:'#FEF2F2',color:T.red,borderRadius:'4px',padding:'1px 6px',fontFamily:FONT,fontWeight:600}}>{toVendre} à vendre</span>}
          </div>

          {/* Barres progression */}
          <div style={{display:'flex',flexDirection:'column',gap:'4px'}}>
            {pctSerie!==null && (
              <div style={{display:'flex',alignItems:'center',gap:'6px'}}>
                <span style={{fontSize:'10px',fontWeight:600,color:T.muted,fontFamily:FONT,width:'54px',flexShrink:0}}>Série</span>
                <Bar pct={pctSerie} color={complete?T.green:T.orange}/>
                <span style={{fontSize:'10px',fontWeight:700,color:complete?T.green:T.orange,fontFamily:FONT,width:'34px',textAlign:'right',flexShrink:0}}>{uniqueNums}/{total}</span>
              </div>
            )}
            <div style={{display:'flex',alignItems:'center',gap:'6px'}}>
              <span style={{fontSize:'10px',fontWeight:600,color:T.muted,fontFamily:FONT,width:'54px',flexShrink:0}}>Lu</span>
              <Bar pct={pctLus} color={T.blue}/>
              <span style={{fontSize:'10px',fontWeight:700,color:T.blue,fontFamily:FONT,width:'34px',textAlign:'right',flexShrink:0}}>{lus}/{bds.length}</span>
            </div>
          </div>
        </div>

        {/* Chevron */}
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" style={{flexShrink:0,transform:expanded?'rotate(180deg)':'none',transition:'transform .2s',color:T.muted}}>
          <path d="M5 7l4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>

      {/* Corps étendu */}
      {expanded && (
        <div style={{borderTop:`1px solid ${T.border}`,padding:'14px 16px',background:'#FDFAF8'}}>
          <button onClick={()=>onShelf(serie)}
            style={{width:'100%',marginBottom:'12px',padding:'10px',border:`1.5px solid ${T.orange}`,borderRadius:'10px',background:'var(--orange-pale)',color:T.orange,fontFamily:FONT,fontSize:'13px',fontWeight:700,cursor:'pointer'}}>
            🗄️ Voir l'étagère de cette série
          </button>
          {/* Étagère livres */}
          <div style={{display:'flex',flexWrap:'wrap',gap:'5px',marginBottom:'12px',alignItems:'flex-end'}}>
            {sorted.map(bd=><BookThumb key={bd.id} bd={bd} onClick={onEditBD}/>)}
          </div>

          {/* Légende */}
          <div style={{display:'flex',gap:'10px',flexWrap:'wrap',borderTop:`1px solid ${T.border}`,paddingTop:'10px',alignItems:'center'}}>
            {STATUTS.filter(s=>bds.some(b=>b.statut===s.value)).map(s=>(
              <div key={s.value} style={{display:'flex',alignItems:'center',gap:'4px',fontSize:'11px',fontWeight:600,color:T.muted,fontFamily:FONT}}>
                <span style={{width:'8px',height:'8px',borderRadius:'50%',background:s.color,display:'inline-block'}}/>
                {s.label}
              </div>
            ))}
            <div style={{marginLeft:'auto',fontSize:'11px',color:T.muted,fontFamily:FONT,fontWeight:500}}>{lus}/{bds.length} lu{lus>1?'s':''}</div>
          </div>
          {/* Tomes manquants */}
          {(() => {
            const missing = findMissingTomes(bds, totals[serie]);
            if (missing.length === 0) return null;
            // Grouper en plages consécutives pour les grandes listes
            // ex: [3,4,5,8,9,12] → "T.3–5, T.8–9, T.12"
            const ranges = [];
            let start = missing[0], end = missing[0];
            for (let i = 1; i <= missing.length; i++) {
              if (i < missing.length && missing[i] === end + 1) {
                end = missing[i];
              } else {
                ranges.push(start === end ? `T.${start}` : end === start+1 ? `T.${start}, T.${end}` : `T.${start}–${end}`);
                if (i < missing.length) { start = missing[i]; end = missing[i]; }
              }
            }
            return (
              <div style={{marginTop:'10px',paddingTop:'10px',borderTop:`1px dashed ${T.border}`}}>
                <div style={{fontSize:'11px',fontWeight:700,color:T.red,fontFamily:FONT,marginBottom:'6px',display:'flex',alignItems:'center',gap:'6px'}}>
                  <span>⚠️ {missing.length} tome{missing.length>1?'s':''} manquant{missing.length>1?'s':''}</span>
                  {missing.length>15&&<span style={{fontSize:'10px',fontWeight:500,color:T.subtle}}>groupés par plages</span>}
                </div>
                <div style={{display:'flex',flexWrap:'wrap',gap:'5px'}}>
                  {missing.length<=15
                    ? missing.map(n=>(
                        <span key={n} style={{fontSize:'11px',fontWeight:700,background:'#FEF2F2',color:T.red,border:'1px solid #FECACA',borderRadius:'6px',padding:'2px 8px',fontFamily:FONT}}>T.{n}</span>
                      ))
                    : ranges.map((r,i)=>(
                        <span key={i} style={{fontSize:'11px',fontWeight:700,background:'#FEF2F2',color:T.red,border:'1px solid #FECACA',borderRadius:'6px',padding:'2px 8px',fontFamily:FONT}}>{r}</span>
                      ))
                  }
                  {!totals[serie] && <span style={{fontSize:'11px',color:T.subtle,fontFamily:FONT,fontStyle:'italic'}}>Indique le total pour voir tous les manquants</span>}
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}

// ── Vue principale ─────────────────────────────────────────────────────────────
export default function SeriesView({ bds, onEditBD }) {
  const [search, setSearch]   = useState('');
  const [sortBy, setSortBy]   = useState('alpha');
  const [totals, setTotals]   = useState(loadTotals);
  const [hsTotals, setHsTotals] = useState(loadHsTotals);
  const syncVer = useSeriesSettings();
  useEffect(()=>{ if (syncVer>0) { setTotals(loadTotals()); setHsTotals(loadHsTotals()); } }, [syncVer]);
  const [shelfSerie, setShelfSerie] = useState(null);

  // Persister les totaux à chaque changement
  useEffect(()=>{ saveTotals(totals); }, [totals]);
  useEffect(()=>{ saveHsTotals(hsTotals); }, [hsTotals]);
  const handleHsChange = (serie, val) => setHsTotals(t => { const n={...t}; if (val===null) delete n[serie]; else n[serie]=val; return n; });

  const handleTotalChange = (serie, val) => {
    setTotals(t => {
      const next = {...t};
      if (val===null) delete next[serie]; else next[serie]=val;
      return next;
    });
  };

  const grouped = useMemo(() => {
    let entries = Object.entries(
      bds.reduce((acc,b)=>{
        const k = b.serie?.trim() || '— Sans série —';
        if(!acc[k]) acc[k]=[];
        acc[k].push(b);
        return acc;
      },{})
    );
    if (search) { const s=search.toLowerCase(); entries=entries.filter(([n])=>n.toLowerCase().includes(s)); }
    entries.sort((a,b)=>{
      if (sortBy==='tomes')   return b[1].length-a[1].length;
      if (sortBy==='lu') {
        const pA=a[1].filter(x=>x.lu).length/Math.max(a[1].length,1);
        const pB=b[1].filter(x=>x.lu).length/Math.max(b[1].length,1);
        return pB-pA;
      }
      if (sortBy==='complet') {
        const pA=totals[a[0]]?countUniqueNums(a[1])/totals[a[0]]:0;
        const pB=totals[b[0]]?countUniqueNums(b[1])/totals[b[0]]:0;
        return pB-pA;
      }
      return a[0].localeCompare(b[0],'fr');
    });
    return entries;
  }, [bds,search,sortBy,totals]);

  const totalAlbums  = bds.length;
  const totalSeries  = grouped.length;
  // Une série est complète si tous les numéros 1..total sont possédés
  // (les hors-séries n'entrent pas dans le calcul, ni pour ni contre)
  const completeSeries = grouped.filter(([n,l])=>totals[n]&&findMissingTomes(l,totals[n]).length===0).length;

  return (
    <div>
      {/* Mini stats séries */}
      {completeSeries>0 && (
        <div style={{background:'#F0FDF4',border:'1px solid #BBF7D0',borderRadius:'10px',padding:'10px 14px',marginBottom:'12px',display:'flex',alignItems:'center',gap:'8px',fontSize:'13px',fontWeight:600,color:'#15803D',fontFamily:FONT}}>
          ✓ {completeSeries} série{completeSeries>1?'s':''} complète{completeSeries>1?'s':''} sur {totalSeries}
        </div>
      )}

      {/* Toolbar */}
      <div style={{display:'flex',gap:'8px',alignItems:'center',marginBottom:'12px',flexWrap:'wrap'}}>
        <div style={{position:'relative',flex:1,minWidth:'150px'}}>
          <svg style={{position:'absolute',left:'10px',top:'50%',transform:'translateY(-50%)',pointerEvents:'none',color:'var(--subtle)'}} width="14" height="14" viewBox="0 0 16 16" fill="none">
            <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.5"/><path d="M11 11L14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          <input type="text" value={search} onChange={e=>setSearch(e.target.value)} placeholder="Filtrer une série…"
            style={{width:'100%',padding:'9px 12px 9px 32px',border:`1px solid ${T.border}`,borderRadius:'8px',fontFamily:FONT,fontSize:'13px',background:T.surface,color:T.ink,outline:'none'}}
            onFocus={e=>{e.target.style.borderColor=T.orange;e.target.style.boxShadow=`0 0 0 3px rgba(240,78,26,.10)`;}}
            onBlur={e=>{e.target.style.borderColor=T.border;e.target.style.boxShadow='none';}}/>
        </div>
        <div style={{display:'flex',gap:'4px',flexWrap:'wrap'}}>
          {[['alpha','A→Z'],['tomes','Tomes'],['lu','% Lu'],['complet','% Complet']].map(([v,l])=>(
            <button key={v} onClick={()=>setSortBy(v)}
              style={{padding:'8px 10px',border:`1px solid ${sortBy===v?T.orange:T.border}`,borderRadius:'8px',background:sortBy===v?'var(--orange-pale)':T.surface,color:sortBy===v?T.orange:T.muted,fontFamily:FONT,fontSize:'12px',fontWeight:sortBy===v?700:500,cursor:'pointer',transition:'all .15s'}}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {/* Compteur */}
      <div style={{fontSize:'12px',fontWeight:500,color:T.subtle,marginBottom:'10px',fontFamily:FONT,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
        <span>{grouped.length} série{grouped.length>1?'s':''} · {totalAlbums} album{totalAlbums>1?'s':''}</span>
        <span style={{fontSize:'11px',color:T.subtle}}>Clique sur une série pour l'ouvrir</span>
      </div>

      {/* Liste des séries */}
      {grouped.length === 0 ? (
        <div style={{textAlign:'center',padding:'3.5rem 2rem',background:T.surface,border:`1px solid ${T.border}`,borderRadius:'16px'}}>
          <Mascot variant={search ? 'search' : 'happy'} size={96}/>
          <div style={{fontFamily:FONT,fontSize:'14px',fontWeight:700,color:T.ink,marginBottom:'4px'}}>
            {search ? 'Aucune série ne correspond' : 'Pas encore de série'}
          </div>
          <div style={{fontFamily:FONT,fontSize:'12.5px',color:T.muted}}>
            {search ? 'Essaie un autre nom.' : 'Tes albums avec un nom de série apparaîtront ici, groupés automatiquement.'}
          </div>
        </div>
      ) : (
        <div style={{display:'flex',flexDirection:'column',gap:'8px'}}>
          {grouped.map(([name,list])=>(
            <SerieCard key={name} serie={name} bds={list} onEditBD={onEditBD}
              totals={totals} onTotalChange={handleTotalChange}
              hsTotals={hsTotals} onHsChange={handleHsChange} onShelf={setShelfSerie}/>
          ))}
        </div>
      )}
      {shelfSerie && (
        <ShelfModal serie={shelfSerie} allBds={bds} totals={totals} hsTotals={hsTotals}
          onClose={()=>setShelfSerie(null)} onEditBD={onEditBD}/>
      )}
    </div>
  );
}
